import { initComponent } from './core/init-component';
import { bindAll } from './utils/bind-all';
import { initCursorFollowClose } from './utils/cursor-follow-close';
import Swiper from 'swiper';
import 'swiper/css';
import { Mousewheel } from 'swiper/modules';

initComponent('.js-pdp-main', (section) => {
  const medias = [...section.querySelectorAll<HTMLElement>('.js-pdp-media')];
  const thumbs = [...section.querySelectorAll<HTMLElement>('.js-pdp-thumb')];  
  const atcButtons = [...section.querySelectorAll<HTMLElement>('.js-pdp-atc-button')];
  const mobileGallery = section.querySelector<HTMLElement>('.js-pdp-mobile-gallery');
  const mobileContent = section.querySelector<HTMLElement>('.js-pdp-mobile-content');
  const stickyAtc = section.querySelector<HTMLElement>('.js-pdp-mobile-atc');
  const colourButtons = [...section.querySelectorAll<HTMLButtonElement>('[data-selector="colour"] [data-value]')];
  const sizeButtons = [...section.querySelectorAll<HTMLButtonElement>('[data-selector="size"] [data-value]')];
  const variants = JSON.parse(section.querySelector<HTMLScriptElement>('[data-product-variants]')?.textContent || '[]');  
  const variantPrices = JSON.parse(section.querySelector<HTMLScriptElement>('[data-product-variant-prices]')?.textContent || '{}');
  const priceCurrentEls = [...section.querySelectorAll<HTMLElement>('.js-pdp-price-current')];
  const priceCompareEls = [...section.querySelectorAll<HTMLElement>('.js-pdp-price-compare')];
  const initialPriceCurrentTexts = priceCurrentEls.map((el) => el.textContent ?? '');
  const initialPriceCurrentRed = priceCurrentEls.map((el) => el.classList.contains('text-brand-red'));
  const initialPriceCompareTexts = priceCompareEls.map((el) => el.textContent ?? '');
  const initialPriceCompareHidden = priceCompareEls.map((el) => el.classList.contains('hidden'));
  const mobileAtcAnchor = section.querySelector<HTMLElement>('.js-pdp-mobile-atc-anchor');
  const colourPosition = Number(section.dataset.colourPosition);
  const sizePosition = Number(section.dataset.sizePosition);
  const sizeToast = section.querySelector<HTMLElement>('.js-pdp-size-toast');
  const colourToast = section.querySelector<HTMLElement>('.js-pdp-colour-toast');
  const header = document.querySelector('.js-header') as HTMLElement | null;
  const footer = document.querySelector('.js-footer-wrap') as HTMLElement | null;
  const footerSection = document.querySelector<HTMLElement>('.shopify-section-group-footer-group');
  const desktopLayout = section.querySelector<HTMLElement>('.js-pdp-desktop-layout');
  const overlay = section.querySelector<HTMLElement>('.js-pdp-overlay');
  const overlayContent = section.querySelector<HTMLElement>('.js-pdp-overlay-content');
  let selectedColour: string | null = null;
  let selectedSize: string | null = null;
  

  // PDP header clone
  let headerClone: HTMLElement | null = null;

  const updateHeaderPosition = () => {
    if (!header || !overlayContent) return;
  
    if (window.innerWidth >= 1024) {
      if (!headerClone) {
        headerClone = header.cloneNode(true) as HTMLElement;
        headerClone.classList.remove('js-header');
        headerClone.classList.remove(
          'fixed',
          'bg-grey-800',
          '-translate-y-full',
          'opacity-0',
          'pointer-events-none',
          'js-header-pdp-initial-hidden'
        );
        headerClone.classList.add('js-pdp-header-clone', 'translate-y-0');
      }

      if (headerClone.parentElement !== overlayContent) {
        overlayContent.prepend(headerClone);
      }
    } else {
      headerClone?.remove();
    }
  };
  
  updateHeaderPosition();
  window.addEventListener('resize', updateHeaderPosition);

  // Variant picker

  const getVariantOption = (variant: any, position: number) => {
    return variant[`option${position}`];
  };

  const getSingleOptionValue = (buttons: HTMLButtonElement[]) => {
    const values = [
      ...new Set(
        buttons
          .map((button) => button.dataset.value)
          .filter(Boolean)
      )
    ];

    return values.length === 1 ? values[0] : null;
  };

  const initializeVariantSelection = () => {
    const singleAvailableVariant =
      variants.length === 1 && variants[0]?.available
        ? variants[0]
        : null;

    if (singleAvailableVariant) {
      selectedColour =
        colourPosition
          ? getVariantOption(singleAvailableVariant, colourPosition)
          : null;

      selectedSize =
        sizePosition
          ? getVariantOption(singleAvailableVariant, sizePosition)
          : null;

      return;
    }

    const firstAvailableColourButton = colourButtons.find((button) => (
      button.dataset.value &&
      button.dataset.oos !== 'true'
    ));

    if (firstAvailableColourButton?.dataset.value) {
      selectedColour = firstAvailableColourButton.dataset.value;
    }

    const singleSize = getSingleOptionValue(sizeButtons);

    if (
      selectedColour &&
      singleSize &&
      variants.some((variant: any) => (
        variant.available &&
        getVariantOption(variant, colourPosition) === selectedColour &&
        getVariantOption(variant, sizePosition) === singleSize
      ))
    ) {
      selectedSize = singleSize;
    }
  };  

  const updateSelectionState = () => {
    colourButtons.forEach((button) => {
      setButtonState(
        button,
        button.dataset.value === selectedColour
      );
    });
  
    sizeButtons.forEach((button) => {
      setButtonState(
        button,
        button.dataset.value === selectedSize
      );
    });
  
    updateAtbState();
    updatePdpPrice();
  };  

  const setButtonState = (button: HTMLButtonElement, active: boolean) => {
    const isSelectableOOS = button.dataset.oos === 'true' && (
      button.dataset.optionType === 'colour-selector' ||
      button.dataset.optionType === 'size-selector'
    );

    if (button.dataset.oos === 'true' && !isSelectableOOS) return;

    button.setAttribute('aria-pressed', active ? 'true' : 'false');

    button.classList.toggle('bg-surface-white', active);
    button.classList.toggle('bg-surface-white-15', !active);

    const contentSelector = isSelectableOOS
      ? '.js-selector-oos'
      : '.js-selector-normal';
    const normal = button.querySelector<HTMLElement>(contentSelector);
    const text = normal?.querySelector<HTMLElement>('span');
    const icon = normal?.querySelector<HTMLElement>('svg');

    if (!text) return;

    text.classList.toggle('text-surface-white', active);
    text.classList.toggle('mix-blend-difference', active);
    text.classList.toggle('text-text-primary', !active && !isSelectableOOS);
    text.classList.toggle('text-text-secondary', !active && isSelectableOOS);

    icon?.classList.toggle('text-surface-white', active);
    icon?.classList.toggle('mix-blend-difference', active);
    icon?.classList.toggle('text-text-secondary', !active && isSelectableOOS);

    if (isSelectableOOS) {
      normal?.classList.toggle('bg-surface-white', active);
      normal?.classList.toggle('bg-surface-primary', !active);
    }
  };

  const setButtonOOS = (
    button: HTMLButtonElement,
    isOOS: boolean,
    optionType: 'colour' | 'size'
  ) => {
    const normal = button.querySelector<HTMLElement>('.js-selector-normal');
    const oos = button.querySelector<HTMLElement>('.js-selector-oos');

    if (!normal || !oos) return;

    if (isOOS) {
      button.dataset.oos = 'true';
      button.classList.remove('js-bis-trigger');

      normal.classList.add('hidden');
      oos.classList.remove('hidden');

      const isSelected =
        (optionType === 'colour' && selectedColour === button.dataset.value) ||
        (optionType === 'size' && selectedSize === button.dataset.value);

      if (isSelected) {
        button.classList.remove('bg-surface-white-15');
        button.classList.add('bg-surface-white');
      } else {
        button.classList.remove('bg-surface-white');
        button.classList.add('bg-surface-white-15');
      }

      button.classList.remove('flex-col', 'items-center');
      button.classList.add('items-start', 'justify-center');

      return;
    }

    delete button.dataset.oos;
    button.classList.remove('js-bis-trigger');

    oos.classList.add('hidden');
    normal.classList.remove('hidden');

    button.classList.remove('items-start', 'justify-center');
    button.classList.add('flex-col', 'items-center');
  };

  const updateAtbState = () => {
    const isReady =
      !!selectedColour &&
      !!selectedSize &&
      isVariantCombinationAvailable(selectedColour, selectedSize);
  
    atcButtons.forEach((button) => {
      button.classList.toggle(
        'active',
        isReady
      );
  
      button.classList.toggle(
        'bg-surface-white',
        isReady
      );
  
      button.classList.toggle(
        'text-text-invert',
        isReady
      );
    });
  };

  const isVariantCombinationAvailable = (
    colour: string | null,
    size: string | null
  ) => {
    return variants.some((variant: any) => {
      if (!variant.available) return false;

      if (
        colour &&
        getVariantOption(variant, colourPosition) !== colour
      ) {
        return false;
      }

      if (
        size &&
        getVariantOption(variant, sizePosition) !== size
      ) {
        return false;
      }

      return true;
    });
  };

  const getVariantCombination = (
    colour: string | null,
    size: string | null
  ) => {
    if (colourPosition && colourButtons.length && !colour) return null;
    if (sizePosition && sizeButtons.length && !size) return null;

    return variants.find((variant: any) => {
      if (
        colour &&
        getVariantOption(variant, colourPosition) !== colour
      ) {
        return false;
      }

      if (
        size &&
        getVariantOption(variant, sizePosition) !== size
      ) {
        return false;
      }

      return true;
    }) ?? null;
  };

  const getVariantById = (variantId?: string) => {
    if (!variantId) return null;

    return variants.find((variant: any) => (
      String(variant.id) === String(variantId)
    )) ?? null;
  };

  const getVariantForSelection = (fallbackVariantId?: string) => (
    getVariantCombination(selectedColour, selectedSize) ??
    getVariantById(fallbackVariantId) ??
    variants.find((variant: any) => {
      if (
        selectedColour &&
        getVariantOption(variant, colourPosition) !== selectedColour
      ) {
        return false;
      }

      if (
        selectedSize &&
        getVariantOption(variant, sizePosition) !== selectedSize
      ) {
        return false;
      }

      return true;
    }) ??
    null
  );

  // `behavior` is 'instant' when landing from a URL preselection: the page must open already on
  // the colour's image, and animating a scroll on load reads as a glitch.
  const scrollToVariantMedia = (
    variant: any,
    behavior: ScrollBehavior = 'smooth'
  ) => {
    const mediaId = variant?.featured_media?.id;

    if (!mediaId) return;

    const targets = [
      ...section.querySelectorAll<HTMLElement>(`[data-media-id="${mediaId}"]`)
    ];
    const target = targets.find((el) => el.offsetParent !== null) ?? targets[0];

    if (!target) return;

    const isMobileTarget = !!target.closest('.js-pdp-mobile-gallery');

    target.scrollIntoView({
      behavior,
      block: isMobileTarget ? 'nearest' : 'start',
      inline: 'start'
    });
  };

  const resetPdpPrice = () => {
    priceCurrentEls.forEach((el, index) => {
      el.textContent = initialPriceCurrentTexts[index] ?? '';
      el.classList.toggle('text-brand-red', initialPriceCurrentRed[index] ?? false);
    });

    priceCompareEls.forEach((el, index) => {
      el.textContent = initialPriceCompareTexts[index] ?? '';
      el.classList.toggle('hidden', initialPriceCompareHidden[index] ?? true);
    });
  };

  const updatePdpPrice = () => {
    const variant = getVariantCombination(selectedColour, selectedSize);
    const price = variant ? variantPrices[String(variant.id)] : null;

    if (!price?.price) {
      resetPdpPrice();
      return;
    }

    priceCurrentEls.forEach((el) => {
      el.textContent = price.price;
      el.classList.toggle('text-brand-red', !!price.comparePrice);
    });

    priceCompareEls.forEach((el) => {
      el.textContent = price.comparePrice ?? '';
      el.classList.toggle('hidden', !price.comparePrice);
    });
  };

  const getSelectedVariant = () => {
    return variants.find((variant: any) => {
      if (
        selectedColour &&
        getVariantOption(variant, colourPosition) !== selectedColour
      ) {
        return false;
      }

      if (
        selectedSize &&
        getVariantOption(variant, sizePosition) !== selectedSize
      ) {
        return false;
      }

      return true;
    }) ?? null;
  };

  const openBisDrawerForVariant = (variant: any) => {
    const productId =
      colourButtons[0]?.dataset.product ??
      sizeButtons[0]?.dataset.product;

    if (!variant || variant.available || !productId) return;

    document.dispatchEvent(
      new CustomEvent('bis:open', {
        detail: {
          product: productId,
          variant: String(variant.id),
        },
      })
    );
  };

  const openBisDrawerForSelection = () => {    
    openBisDrawerForVariant(
      getVariantCombination(selectedColour, selectedSize)
    );
  };

  const openBisDrawerForColourSelection = (
    colour: string | null,
    size: string | null
  ) => {
    if (!colour || !size) return;

    openBisDrawerForVariant(
      getVariantCombination(colour, size)
    );
  };

  const updateOptionAvailability = (
    buttons: HTMLButtonElement[],
    optionPosition: number,
    pairedPosition: number,
    pairedValue: string | null,
    optionType: 'colour' | 'size'
  ) => {
    buttons.forEach((button) => {
      const value = button.dataset.value;

      const matchingVariants = variants.filter((variant: any) => {
        if (getVariantOption(variant, optionPosition) !== value) {
          return false;
        }

        if (!pairedValue) {
          return true;
        }

        return getVariantOption(variant, pairedPosition) === pairedValue;
      });

      const matchingVariant =
        matchingVariants.find((variant: any) => variant.available) ??
        matchingVariants[0];
      const available = matchingVariants.some((variant: any) => variant.available);
      const optionExists = matchingVariants.length > 0;

      if (optionType === 'size') {
        button.classList.toggle(
          'hidden',
          !!pairedValue && !optionExists
        );
      }

      if (matchingVariant?.id) {
        button.dataset.variant = String(matchingVariant.id);
      }

      setButtonOOS(button, !available, optionType);
    });
  };

  const updateAvailability = (
    updateColours = true,
    updateSizes = true
  ) => {
    if (updateColours && colourPosition) {
      updateOptionAvailability(
        colourButtons,
        colourPosition,
        sizePosition,
        selectedSize,
        'colour'
      );
    }

    if (updateSizes && sizePosition) {
      updateOptionAvailability(
        sizeButtons,
        sizePosition,
        colourPosition,
        selectedColour,
        'size'
      );
    }
  };

  const updateDesktopColourVisibility = (
    root: HTMLElement | null
  ) => {
    if (window.innerWidth < 768 || !root) return;
  
    const colourSelector = root.querySelector<HTMLElement>(
      '[data-selector="colour"]'
    );
  
    const sizeSelector = root.querySelector<HTMLElement>(
      '[data-selector="size"]'
    );
  
    const atb = root.querySelector<HTMLElement>(
      '.js-pdp-atc-button'
    );
  
    if (
      !colourSelector ||
      !sizeSelector ||
      !atb
    ) {
      return;
    }
  
    const buttons = [
      ...colourSelector.querySelectorAll<HTMLButtonElement>(
        '[data-value]'
      )
    ];
    const moreButton = colourSelector.querySelector<HTMLButtonElement>(
      '.js-pdp-open-colour-drawer'
    );
    const colourCount = Number(
      colourSelector.dataset.colourCount || buttons.length
    );
    const hasUnrenderedColours = colourCount > buttons.length;
  
    buttons.forEach((button) => {
      button.classList.remove('hidden');
    });

    moreButton?.classList.toggle('hidden', !hasUnrenderedColours);
  
    let hiddenCount = 0;
  
    const hasEnoughColourSpace = () => {
      const colourRect = colourSelector.getBoundingClientRect();
      const sizeRect = sizeSelector.getBoundingClientRect();
      const atbRect = atb.getBoundingClientRect();
  
      const gap1 = sizeRect.left - colourRect.right;
      const gap2 = atbRect.left - sizeRect.right;
      const visibleColourButtonCount = buttons.filter((button) => {
        return !button.classList.contains('hidden');
      }).length;
      const visibleControlCount =
        visibleColourButtonCount +
        (moreButton && !moreButton.classList.contains('hidden') ? 1 : 0);

      return (
        Math.min(gap1, gap2) >= 24 &&
        visibleControlCount * 96 <= colourSelector.clientWidth &&
        colourSelector.scrollWidth <= colourSelector.clientWidth
      );
    };
  
    while (
      !hasEnoughColourSpace() &&
      buttons.length - hiddenCount > 1
    ) {
      const button = buttons[buttons.length - hiddenCount - 1];
  
      button.classList.add('hidden');
      moreButton?.classList.remove('hidden');
  
      hiddenCount++;
    }

    moreButton?.classList.toggle(
      'hidden',
      hiddenCount === 0 && !hasUnrenderedColours
    );
  };
  
  requestAnimationFrame(() => {
    updateDesktopColourVisibility(
      section.querySelector('.js-pdp-variant-selector-desktop')
    );
  
    updateDesktopColourVisibility(
      stickyVariantSelector
    );
  });
  
  window.addEventListener('resize', () => {
    requestAnimationFrame(() => {
      updateDesktopColourVisibility(
        section.querySelector('.js-pdp-variant-selector-desktop')
      );
  
      updateDesktopColourVisibility(
        stickyVariantSelector
      );
    });
  });

  bindAll(
    section,
    '[data-selector="colour"] [data-value]',
    'click',
    (event: Event) => {
      const button = event.currentTarget as HTMLButtonElement;

      if (
        button.dataset.oos === 'true' &&
        button.dataset.optionType !== 'colour-selector'
      ) return;
  
      selectedColour = button.dataset.value || null;

      openBisDrawerForColourSelection(selectedColour, selectedSize);

      if (
        selectedSize &&
        !getVariantCombination(selectedColour, selectedSize)
      ) {
        selectedSize = null;
      }

      colourButtons.forEach((btn) => {
        setButtonState(btn, btn === button);
      });
      
      updateDrawerSelection()
      updateAvailability(false, true)
      colourButtons.forEach((btn) => {
        setButtonState(btn, btn.dataset.value === selectedColour);
      });
      sizeButtons.forEach((btn) => {
        setButtonState(btn, btn.dataset.value === selectedSize);
      });
      updateAtbState()
      updatePdpPrice()
      scrollToVariantMedia(getVariantForSelection(button.dataset.variant));
    }
  );
  
  bindAll(
    section,
    '[data-selector="size"] [data-value]',
    'click',
    (event: Event) => {
      const button = event.currentTarget as HTMLButtonElement;
  
      selectedSize = button.dataset.value || null;      
  
      sizeButtons.forEach((btn) => {
        setButtonState(btn, btn === button);
      });
      
      updateAvailability();
      colourButtons.forEach((btn) => {
        setButtonState(btn, btn.dataset.value === selectedColour);
      });
      updateAtbState()
      updatePdpPrice()
      openBisDrawerForSelection();
    }
  );

  // Gallery

  const scrollThumbIntoView = (thumb: HTMLElement) => {
    const container = thumb.closest<HTMLElement>('.js-pdp-gallery-thumbs-scroll');

    if (!container) return;

    const thumbRect = thumb.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const edgeOffset = 80;

    if (thumbRect.top < containerRect.top + edgeOffset) {
      container.scrollBy({
        top: thumbRect.top - containerRect.top - edgeOffset,
        behavior: 'smooth',
      });
    } else if (thumbRect.bottom > containerRect.bottom - edgeOffset) {
      container.scrollBy({
        top: thumbRect.bottom - containerRect.bottom + edgeOffset,
        behavior: 'smooth',
      });
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const index = entry.target.getAttribute('data-media-index');
        

        thumbs.forEach((thumb) => {
          thumb.classList.remove('border-white');
        });

        const activeThumbs = [
          ...section.querySelectorAll<HTMLElement>(
            `[data-thumb-index="${index}"]`
          )
        ];
        
        activeThumbs.forEach((thumb) => {
          thumb.classList.add('border-white');
          scrollThumbIntoView(thumb);
        });       
      });
    },
    {
      threshold: 0.6
    }
  );

  medias.forEach((media) => {
    observer.observe(media);
  });

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const index = thumb.getAttribute('data-thumb-index');

      const target = gallery?.querySelector<HTMLElement>(
        `[data-media-index="${index}"]`
      );

      if (!target) return;

      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  });

  // Preselect colour/size from the URL params emitted by the product card
  // (ls_colour / ls_size). A colour-only link selects just the colour; a size
  // link carries its colour too. The size must match an in-stock button, but the
  // COLOUR does not: a colourway card links to its own colour, and dropping the
  // param when that colour is sold out lands the PDP on an unrelated colour
  // instead. This mirrors the colour chips, which stay selectable when OOS and
  // offer the back-in-stock drawer.
  const applyUrlSelection = () => {
    const params = new URLSearchParams(window.location.search);
    const colourParam = params.get('ls_colour');
    const sizeParam = params.get('ls_size');
    let applied = false;

    if (
      colourParam &&
      colourButtons.some((btn) => btn.dataset.value === colourParam)
    ) {
      selectedColour = colourParam;
      applied = true;
    }

    if (
      sizeParam &&
      sizeButtons.some(
        (btn) => btn.dataset.value === sizeParam && btn.dataset.oos !== 'true'
      ) &&
      (!selectedColour || isVariantCombinationAvailable(selectedColour, sizeParam))
    ) {
      selectedSize = sizeParam;
      applied = true;
    }

    return applied;
  };

  initializeVariantSelection();
  const hasUrlSelection = applyUrlSelection();
  updateAvailability();

  if (hasUrlSelection) {
    // The selector buttons carry transition-colors and this init runs deferred
    // (after first paint), so a plain class change would fade the preselected
    // highlight in — a visible glitch when landing from a product card. Apply
    // the initial state with transitions suppressed so it appears instantly.
    const selectorButtons = [...colourButtons, ...sizeButtons];
    selectorButtons.forEach((btn) => (btn.style.transition = 'none'));
    updateSelectionState();
    void section.offsetWidth; // force reflow so the change commits without animating
    requestAnimationFrame(() => {
      selectorButtons.forEach((btn) => btn.style.removeProperty('transition'));
    });
    // Same move a colour chip click ends on, so landing from a card matches clicking the chip.
    scrollToVariantMedia(getVariantForSelection(), 'instant');
  } else {
    updateSelectionState();
  }

  const updateMobileGalleryHeight = () => {
    if (
      window.innerWidth >= 1024 ||
      !mobileGallery ||
      !mobileContent ||
      !stickyAtc
    ) {
      section.classList.remove('opacity-0');
      return;
    }

    const viewportHeight = window.innerHeight;

    const contentHeight =
      mobileContent.offsetHeight;

    const atcHeight =
      stickyAtc.offsetHeight;

    const galleryHeight =
      viewportHeight -
      contentHeight -
      atcHeight - 66;

    mobileGallery.style.height =
      `${galleryHeight}px`;

    section.classList.remove('opacity-0');
  };

  updateMobileGalleryHeight();

  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    const currentWidth = window.innerWidth;

    if (currentWidth === lastWidth) {
      return;
    }

    lastWidth = currentWidth;

    updateMobileGalleryHeight();
  });

  // Zoom
  const zoomButton = section.querySelector<HTMLElement>('.js-pdp-zoom-in');
  const zoomOutButton = section.querySelector<HTMLElement>('.js-pdp-zoom-out');
  let mouseX = 0;
  let mouseY = 0;
  let zoomTargetMedia: HTMLElement | null = null;

  const activeZoomButton = () => {
    return zoomActive
      ? zoomOutButton
      : zoomButton;
  };

  let hoveringMedia = false;

  medias.forEach((media) => {
    const imageButton = media.querySelector('button');

    if (!imageButton) return;

    imageButton.addEventListener('mouseenter', () => {
      hoveringMedia = true;

      activeZoomButton()?.classList.remove(
        'hidden',
        'opacity-0'
      );
    });

    imageButton.addEventListener('mouseleave', () => {
      hoveringMedia = false;

      activeZoomButton()?.classList.add('opacity-0');
    });

    imageButton.addEventListener('mousemove', (event) => {
      mouseX = event.clientX;
      mouseY = event.clientY;

      const button = activeZoomButton();

      if (!button) return;

      button.style.left = `${event.clientX}px`;
      button.style.top = `${event.clientY}px`;
    });
  });

  window.addEventListener('scroll', () => {
    if (hoveringMedia) {
      activeZoomButton()?.classList.remove('opacity-0');
    }
  });
  
  const gallery = section.querySelector<HTMLElement>('.js-pdp-gallery');  
  const galleryThumbs = section.querySelector<HTMLElement>('.js-pdp-gallery-thumbs');
  const desktopVariantSelector = section.querySelector<HTMLElement>('.js-pdp-variant-selector-desktop');

  let zoomActive = false;

  gallery?.addEventListener('scroll', () => {
    if (!zoomActive) return;
  
    let closestMedia: HTMLElement | null = null;
    let closestDistance = Infinity;
  
    medias.forEach((media) => {
      const rect = media.getBoundingClientRect();
      const distance = Math.abs(rect.top);
  
      if (distance < closestDistance) {
        closestDistance = distance;
        closestMedia = media;
      }
    });
  
    const index = (closestMedia as HTMLElement | null)?.dataset.mediaIndex;
  
    thumbs.forEach((thumb) => {
      const isActive = thumb.dataset.thumbIndex === index;

      thumb.classList.toggle('border-white', isActive);

      if (isActive) scrollThumbIntoView(thumb);
    });
  });

  const openZoom = () => {
    if (!gallery || zoomActive) return;

    zoomActive = true;

    zoomOutButton?.classList.remove('hidden', 'opacity-0');

    document.body.classList.add('overflow-hidden');    

    gallery.classList.add(
      'max-md:!fixed',
      'inset-0',
      'z-60',
      'h-screen',
      'overflow-y-scroll',
      'bg-surface-primary'
    );

    zoomButton?.classList.add('hidden', 'opacity-0');
    desktopVariantSelector?.classList.add('!hidden');
    header?.classList.add('hidden');
    footer?.classList.add('hidden');
    overlay?.classList.add('hidden');
    galleryThumbs?.classList.remove('hidden');

    medias.forEach((media) => {
      const button = media.querySelector<HTMLElement>('button');
      const image = media.querySelector<HTMLElement>('button img');

      image?.classList.add('w-full');
      button?.classList.remove('h-screen');
      button?.classList.add(
        'max-w-none',
        'w-screen'
      );
    });

    zoomButton?.classList.add('hidden');
    zoomOutButton?.classList.remove('hidden');

    updateZoomButtonsPosition();

    requestAnimationFrame(() => {              
      zoomTargetMedia?.scrollIntoView({
        block: 'start',
        behavior: 'instant'
      });
    });
  };

  const closeZoom = () => {
    if (!gallery || !zoomActive) return;

    zoomActive = false;

    document.body.classList.remove('overflow-hidden');

    gallery.classList.remove(
      'max-md:!fixed',
      'inset-0',
      'z-60',
      'h-screen',
      'overflow-y-scroll',
      'bg-surface-primary'
    );

    zoomButton?.classList.remove('hidden', 'opacity-0');
    zoomOutButton?.classList.add('hidden','opacity-0');
    desktopVariantSelector?.classList.remove('!hidden');
    header?.classList.remove('hidden');
    footer?.classList.remove('hidden');
    overlay?.classList.remove('hidden');
    galleryThumbs?.classList.add('hidden');

    medias.forEach((media) => {
      const button = media.querySelector<HTMLElement>('button');
      const image = media.querySelector<HTMLElement>('button image');

      image?.classList.remove('w-full');
      button?.classList.add('h-screen');
      button?.classList.remove(
        'max-w-none',
        'w-screen'
      );
    });

    zoomOutButton?.classList.add('hidden');
    zoomButton?.classList.remove('hidden');

    updateZoomButtonsPosition();

    requestAnimationFrame(() => {
      zoomTargetMedia?.scrollIntoView({
        block: 'start',
        behavior: 'instant'
      });
    });
  };

  const updateZoomButtonsPosition = () => {
    if (zoomButton) {
      zoomButton.style.left = `${mouseX}px`;
      zoomButton.style.top = `${mouseY}px`;
    }
  
    if (zoomOutButton) {
      zoomOutButton.style.left = `${mouseX}px`;
      zoomOutButton.style.top = `${mouseY}px`;
    }
  };

  window.addEventListener('mousemove', (event) => {
    mouseX = event.clientX;
    mouseY = event.clientY;
  
    updateZoomButtonsPosition();
  });

  medias.forEach((media) => {
    const imageButton = media.querySelector<HTMLButtonElement>('button');
    
    if (!imageButton) return;

    imageButton.addEventListener('click', () => {
      zoomTargetMedia = media;

      if (zoomActive) {
        closeZoom();
        return;
      }

      openZoom();
    });
  });

  zoomOutButton?.addEventListener('click', closeZoom);

  // Mobile zoom

  bindAll(
    section,
    '.js-pdp-mobile-gallery [data-media-index]',
    'click',
    (event: Event) => {
      const slide = event.currentTarget as HTMLElement;
      const index = slide.dataset.mediaIndex;              
      desktopLayout?.classList.remove('hidden');  

      openZoom();      
      
      gallery?.querySelector<HTMLElement>(
        `[data-media-index="${index}"]`
      )?.scrollIntoView({
        block: 'start'
      });
    }
  );

  bindAll(
    section,
    '.js-pdp-zoom-close',
    'click',
    (event: Event) => {
      desktopLayout?.classList.add('hidden');
      window.scrollTo(0, 0);
      closeZoom();
    }
  );

  // Add to cart 

  const addToCart = async () => {    
    if (colourButtons.length && !selectedColour) {
      colourToast?.classList.add('active');
    
      setTimeout(() => {
        colourToast?.classList.remove('active');
      }, 3000);
    
      return;
    }

    if (sizeButtons.length > 1 && !selectedSize) {      
      sizeToast?.classList.add('active');
    
      setTimeout(() => {
        sizeToast?.classList.remove('active');
      }, 3000);
    
      return;
    }

    const matchedVariant = getSelectedVariant();
  
    if (!matchedVariant) {
      return;
    }

    if (!matchedVariant.available) {
      openBisDrawerForVariant(matchedVariant);
      return;
    }
  
    atcButtons.forEach((button) => button.classList.add('pointer-events-none'))
  
    try {
      // Prefix with the locale root so the cart request keeps the active market/currency (e.g. /en-eu/).
      await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: matchedVariant.id,
              quantity: 1
            }
          ]
        })
      });
      
      window.dispatchEvent(
        new CustomEvent('mini-bag:open')
      );

    } finally {
      atcButtons.forEach((button) => button.classList.remove('pointer-events-none'))
    }
  };

  bindAll(section, '.js-pdp-mobile-atc', 'click', async (event: Event) => {
    event.preventDefault();    

    const button = event.currentTarget as HTMLElement;
    const isSingleVariantProduct = variants.length === 1;

    if (
      window.innerWidth < 1024 &&
      isSingleVariantProduct
    ) {
      await addToCart();
      return;
    }

    if (
      window.innerWidth < 1024 &&
      button.classList.contains('js-pdp-mobile-atc-sticky')
    ) {
      const content = document.querySelector<HTMLElement>(
        '.js-pdp-variant-selector-content'
      );

      if (content?.classList.contains('hidden')) {        
        content.classList.remove('hidden');
        return;
      }
    }
  
    if (window.scrollY < 50 && window.innerWidth < 1024 && mobileAtcAnchor ) {
      mobileAtcAnchor.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });      
    } else {      
      await addToCart()
    }    
  });


  // Colour drawer  

  const colourDrawer = section.querySelector<HTMLElement>('.js-pdp-colour-drawer');
  const colourDrawerSwiperEl = document.querySelector<HTMLElement>('.js-pdp-colour-drawer .swiper');

  let colourDrawerSwiper: Swiper | null = null;

  const initColourDrawerSwiper = () => {
    if (window.innerWidth < 1024) {
      colourDrawerSwiper?.destroy(true, true);
      colourDrawerSwiper = null;
      return;
    }

    if (colourDrawerSwiper || !colourDrawerSwiperEl) return;

    colourDrawerSwiper = new Swiper(colourDrawerSwiperEl, {
      modules: [Mousewheel],
      slidesPerView: 'auto',
      slidesPerGroup: 3,
      spaceBetween: 2,
      slidesOffsetBefore: 8,
      slidesOffsetAfter: 8,
      speed: 300,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true
      },
    });
  };

  initColourDrawerSwiper();

  window.addEventListener('resize', initColourDrawerSwiper);


  if (colourDrawer && !window.Shopify?.designMode) {
    document.body.appendChild(colourDrawer);   
  }
  
  const colourDrawerOverlay = colourDrawer?.querySelector<HTMLElement>('.js-pdp-colour-drawer-overlay');
  const colourDrawerPanel = colourDrawer?.querySelector<HTMLElement>('.js-pdp-colour-drawer-panel');
  const closeButton = colourDrawer?.querySelector<HTMLElement>('.js-pdp-colour-drawer-close');

  // Close icon that follows the cursor over the overlay (md+).
  const colourCloseFollow = colourDrawer?.querySelector<HTMLElement>('.js-drawer-close-follow');
  const colourCursorClose = initCursorFollowClose(colourDrawerOverlay ?? null, colourCloseFollow ?? null);

  const drawerColourButtons = [
    ...document.querySelectorAll<HTMLButtonElement>('.js-pdp-drawer-colour')
  ];
  
  const updateDrawerSelection = () => {
    drawerColourButtons.forEach((button) => {
      const isActive =
        button.dataset.value === selectedColour;
  
      const border = button.querySelector<HTMLElement>(
        '.js-pdp-drawer-colour-border'
      );
  
      border?.classList.toggle('opacity-0', !isActive);
    });
  };
  
  const openColourDrawer = () => {
    if (!colourDrawerOverlay || !colourDrawerPanel) return;

    updateDrawerSelection();
  
    header?.classList.add('opacity-0', 'pointer-events-none');
  
    colourDrawerOverlay.classList.remove(
      'hidden',
      'pointer-events-none'
    );
  
    closeButton?.classList.remove('hidden');
  
    requestAnimationFrame(() => {
      colourDrawerOverlay.classList.remove('opacity-0');
      colourDrawerPanel.classList.remove('translate-x-full');
  
      document.body.classList.add('overflow-hidden');
    });
  };
  
  const closeColourDrawer = () => {
    if (!colourDrawerOverlay || !colourDrawerPanel) return;
  
    header?.classList.remove('opacity-0', 'pointer-events-none');

    closeButton?.classList.add('hidden');

    colourCursorClose.hide();

    colourDrawerOverlay.classList.add('opacity-0');
    colourDrawerPanel.classList.add('translate-x-full');
  
    document.body.classList.remove('overflow-hidden');
  
    setTimeout(() => {
      colourDrawerOverlay.classList.add(
        'hidden',
        'pointer-events-none'
      );
    }, 400);
  };
  
  bindAll(
    section,
    '.js-pdp-open-colour-drawer',
    'click',
    openColourDrawer
  );
  
  bindAll(
    colourDrawer,
    '.js-pdp-colour-drawer-close',
    'click',
    closeColourDrawer
  );
  
  colourDrawerOverlay?.addEventListener(
    'click',
    closeColourDrawer
  );
  
  bindAll(
    colourDrawer,
    '.js-pdp-drawer-colour',
    'click',
    (event: Event) => {
      const button = event.currentTarget as HTMLButtonElement;
  
      const value = button.dataset.value;
  
      if (!value) return;
  
      selectedColour = value;

      openBisDrawerForColourSelection(selectedColour, selectedSize);

      if (
        selectedSize &&
        !getVariantCombination(selectedColour, selectedSize)
      ) {
        selectedSize = null;
      }

      updateAtbState()

      colourButtons.forEach((btn) => {
        setButtonState(
          btn,
          btn.dataset.value === value
        );
      });
  
      updateDrawerSelection();
      updateAvailability(false, true);
      colourButtons.forEach((btn) => {
        setButtonState(btn, btn.dataset.value === selectedColour);
      });
      sizeButtons.forEach((btn) => {
        setButtonState(btn, btn.dataset.value === selectedSize);
      });
      updateAtbState();
      updatePdpPrice();
      scrollToVariantMedia(getVariantForSelection(button.dataset.variant));
  
      closeColourDrawer();
    }
  );

  // Sticky variant selector

  const stickyVariantSelector = section.querySelector<HTMLElement>(
    '.js-pdp-variant-selector-sticky'
  );  
  const main = section.closest('main');
  let stickyVariantSelectorShouldShow = false;
  let footerIsVisible = false;


  if (
    main &&
    stickyVariantSelector &&
    !window.Shopify?.designMode
  ) {
    main.appendChild(stickyVariantSelector);
  }

  const desktopVariantSelectorPrimary = section.querySelector<HTMLElement>(
    '.js-pdp-variant-selector-desktop'
  );

  const updateStickyVariantSelector = (show: boolean) => {
    if (!stickyVariantSelector) return;

    stickyVariantSelectorShouldShow = show;

    if (window.scrollY < 300) {
      show = false;
    }

    if (footerIsVisible) {
      show = false;
    }

    stickyVariantSelector.classList.toggle('translate-y-0', show);
    stickyVariantSelector.classList.toggle('translate-y-full',!show);
    stickyVariantSelector.classList.toggle('pointer-events-none', !show);
  };

  const footerObserver =
    footerSection
      ? new IntersectionObserver(
          ([entry]) => {
            footerIsVisible = entry.isIntersecting;
            updateStickyVariantSelector(stickyVariantSelectorShouldShow);
          },
          {
            threshold: 0,
          }
        )
      : null;

  if (footerSection) {
    footerObserver?.observe(footerSection);
  }

  const mobileSelectorObserver =
    mobileAtcAnchor
      ? new IntersectionObserver(
          ([entry]) => {
            if (window.innerWidth >= 1024) return;

            updateStickyVariantSelector(
              !entry.isIntersecting
            );
          },
          {
            threshold: 0,
          }
        )
      : null;

  if (mobileAtcAnchor) {
    mobileSelectorObserver?.observe(
      mobileAtcAnchor
    );
  }

  const desktopSelectorObserver =
    desktopVariantSelectorPrimary
      ? new IntersectionObserver(
          ([entry]) => {
            if (window.innerWidth < 1024) return;

            updateStickyVariantSelector(
              !entry.isIntersecting
            );
          },
          {
            threshold: 0,
          }
        )
      : null;

  if (desktopVariantSelectorPrimary) {
    desktopSelectorObserver?.observe(
      desktopVariantSelectorPrimary
    );
  } 

  window.addEventListener('pageshow', () => {
    if (
      window.innerWidth >= 1024 ||
      !mobileAtcAnchor
    ) {
      return;
    }
  
    updateStickyVariantSelector(
      mobileAtcAnchor.getBoundingClientRect().bottom < 0
    );
  });

  return {
    unload() {
      observer.disconnect();
      footerObserver?.disconnect();
      window.removeEventListener('resize', updateHeaderPosition);
      headerClone?.remove();
      colourCursorClose.destroy();
    }
  };
});
